// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api, { advisorApi } from './advisorApi';
import type { AdvisorNotification, AdvisorNotificationPage, AdvisorNotificationType, NotificationStatus } from '../types/notification';
import { isNotificationRead } from '../types/notification';

/**
 * Feature flag for using new Notification Service endpoints.
 * Set via environment variable VITE_USE_NEW_NOTIFICATION_SERVICE=true
 *
 * When enabled:
 * - API calls use /notifications instead of /advisor-notifications
 * - SSE connects to /api/notifications/stream instead of /api/advisor-notifications/stream
 *
 * Part of Phase 4: Read Migration (Strangler Fig pattern)
 */
const USE_NEW_NOTIFICATION_SERVICE = import.meta.env.VITE_USE_NEW_NOTIFICATION_SERVICE === 'true';

/**
 * Get API base path based on feature flag.
 * Legacy: /advisor-notifications
 * New: /notifications
 */
const getNotificationBasePath = (): string => {
  return USE_NEW_NOTIFICATION_SERVICE ? '/notifications' : '/advisor-notifications';
};

/**
 * Get SSE stream path based on feature flag.
 * Legacy: /api/advisor-notifications/stream
 * New: /api/notifications/stream
 */
const getSseStreamPath = (): string => {
  return USE_NEW_NOTIFICATION_SERVICE ? '/api/notifications/stream' : '/api/advisor-notifications/stream';
};

/**
 * DESIGN DECISION: Advisor Identification Across Services
 * ========================================================
 * All notification services use the database Advisor.id (UUID) as the recipient identifier,
 * NOT the Keycloak user ID. This ensures consistency across Resource Server, Notification
 * Service, and Advisor UI, and keeps the notification system independent from Keycloak.
 *
 * The advisorId and brokerageId are fetched via GET /api/advisors/me/id and cached
 * for the session. Both are database UUIDs, not subdomains or Keycloak IDs.
 */

export interface GetNotificationsParams {
  page?: number;
  size?: number;
  unreadOnly?: boolean;
  type?: AdvisorNotificationType;
  since?: string;
}

// Cache for advisor ID and brokerage ID to avoid repeated pre-flight requests
// DESIGN DECISION: Use database UUIDs (not Keycloak IDs) for all notification services
export interface AdvisorIdentifiers {
  advisorId: string;
  brokerageId: string;
}
let cachedIdentifiers: AdvisorIdentifiers | null = null;

/**
 * Transform a backend NotificationDTO into the frontend AdvisorNotification shape.
 * The backend returns `status` (PENDING/DELIVERED/READ/ARCHIVED) and `notificationType`,
 * while the frontend expects `isRead` (boolean) and `type`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformNotification = (dto: any): AdvisorNotification => {
  const status: NotificationStatus = dto.status ?? 'PENDING';
  return {
    ...dto,
    type: dto.notificationType ?? dto.type,
    status,
    isRead: isNotificationRead(status),
  };
};

export const notificationApi = {
  /**
   * Set the current brokerageId for API calls.
   * Delegates to advisorApi to maintain consistency across all API modules.
   */
  setBrokerageId: (brokerageId: string): void => {
    advisorApi.setBrokerageId(brokerageId);
  },

  /**
   * Get current advisor's internal ID and brokerage ID via pre-flight request.
   * This is used to avoid database lookups during SSE connection initialization,
   * preventing HikariCP connection pool leak warnings.
   *
   * Both IDs are cached for the session to avoid repeated API calls.
   * Returns database UUIDs (not Keycloak IDs) per design decision.
   *
   * @see features/sse-connection-leak-fix.md for architectural rationale
   */
  getAdvisorIdentifiers: async (): Promise<AdvisorIdentifiers> => {
    // Return cached identifiers if available
    if (cachedIdentifiers) {
      return cachedIdentifiers;
    }

    const response = await api.get<{ advisorId: string; brokerageId: string }>('/advisors/me/id');
    cachedIdentifiers = {
      advisorId: response.data.advisorId,
      brokerageId: response.data.brokerageId,
    };
    return cachedIdentifiers;
  },

  /**
   * Get current advisor's internal ID (convenience wrapper).
   * Delegates to getAdvisorIdentifiers for backward compatibility.
   */
  getAdvisorId: async (): Promise<string> => {
    const identifiers = await notificationApi.getAdvisorIdentifiers();
    return identifiers.advisorId;
  },

  /**
   * Clear cached identifiers (call on logout or session change)
   */
  clearAdvisorIdCache: (): void => {
    cachedIdentifiers = null;
  },

  /**
   * Get paginated notifications with optional filtering.
   * Uses advisorId (database UUID) per design decision - see class-level comment.
   */
  getNotifications: async (params: GetNotificationsParams = {}): Promise<AdvisorNotificationPage> => {
    const { page = 0, size = 20, unreadOnly, type, since } = params;

    // Get advisorId for the query (cached after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    const queryParams = new URLSearchParams();
    queryParams.append('advisorId', identifiers.advisorId);
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());
    if (unreadOnly !== undefined) {
      queryParams.append('unreadOnly', unreadOnly.toString());
    }
    if (type) {
      queryParams.append('type', type);
    }
    if (since) {
      queryParams.append('since', since);
    }

    const basePath = getNotificationBasePath();
    const response = await api.get<AdvisorNotificationPage>(
      `${basePath}?${queryParams.toString()}`
    );
    const data = response.data;
    return {
      ...data,
      content: (data.content ?? []).map(transformNotification),
    };
  },

  /**
   * Get unread notification count for badge display.
   * Uses advisorId (database UUID) per design decision - see class-level comment.
   * Always passes advisorId as query param since BFF forwards it to Notification Service.
   */
  getUnreadCount: async (): Promise<number> => {
    // Get advisorId for the query (cached after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    const basePath = getNotificationBasePath();
    // Use /unread/count for new Notification Service, /unread-count for legacy
    // Always pass advisorId since BFF forwards query params to Notification Service
    const pathSuffix = USE_NEW_NOTIFICATION_SERVICE ? '/unread/count' : '/unread-count';
    const endpoint = `${basePath}${pathSuffix}?advisorId=${encodeURIComponent(identifiers.advisorId)}`;
    const response = await api.get<{ count: number }>(endpoint);
    return response.data.count;
  },

  /**
   * Mark a specific notification as read.
   * Uses advisorId (database UUID) per design decision - see class-level comment.
   * Always passes advisorId as query param since BFF forwards it to Notification Service.
   */
  markAsRead: async (id: string): Promise<void> => {
    // Get advisorId for the query (cached after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    const basePath = getNotificationBasePath();
    // Always pass advisorId since BFF forwards query params to Notification Service
    // BFF converts PUT to POST for /read endpoints when routing to Notification Service
    const endpoint = `${basePath}/${id}/read?advisorId=${encodeURIComponent(identifiers.advisorId)}`;
    await api.put(endpoint);
  },

  /**
   * Mark all notifications as read.
   * Uses advisorId (database UUID) per design decision - see class-level comment.
   * Always passes advisorId as query param since BFF forwards it to Notification Service.
   */
  markAllAsRead: async (): Promise<{ markedCount: number }> => {
    // Get advisorId for the query (cached after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    const basePath = getNotificationBasePath();
    // Always pass advisorId since BFF forwards query params to Notification Service
    // BFF converts PUT to POST for /read-all endpoints when routing to Notification Service
    const endpoint = `${basePath}/read-all?advisorId=${encodeURIComponent(identifiers.advisorId)}`;
    const response = await api.put<{ markedCount: number }>(endpoint);
    return response.data;
  },

  /**
   * Delete a notification.
   * Uses advisorId (database UUID) per design decision - see class-level comment.
   * Always passes advisorId as query param since BFF forwards it to Notification Service.
   */
  deleteNotification: async (id: string): Promise<void> => {
    // Get advisorId for the query (cached after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    const basePath = getNotificationBasePath();
    // Always pass advisorId since BFF forwards query params to Notification Service
    const endpoint = `${basePath}/${id}?advisorId=${encodeURIComponent(identifiers.advisorId)}`;
    await api.delete(endpoint);
  },

  /**
   * Get SSE token for direct Resource Server connections
   * SSE endpoints bypass the BFF, so we need to pass the JWT token as a query parameter
   */
  getSseToken: async (): Promise<string> => {
    const response = await api.get<{ token: string }>('/auth/token');
    return response.data.token;
  },

  /**
   * Create SSE event source for real-time notifications using pre-flight pattern.
   *
   * This method performs two pre-flight requests:
   * 1. GET /api/advisors/me/id - Get advisor's internal ID and brokerage ID (cached for session)
   * 2. GET /api/auth/token - Get authentication token for SSE
   *
   * By passing the advisorId and brokerageId (database UUIDs) to the SSE endpoint,
   * we avoid database lookups during SSE initialization and ensure consistent ID usage
   * across all notification services.
   *
   * DESIGN DECISION: Uses database UUIDs for both advisorId and brokerageId (not Keycloak IDs).
   *
   * @see features/sse-connection-leak-fix.md for architectural rationale
   */
  createEventSource: async (): Promise<EventSource> => {
    // Pre-flight step 1: Get advisor and brokerage IDs (cached, so usually instant after first call)
    const identifiers = await notificationApi.getAdvisorIdentifiers();

    // Pre-flight step 2: Get the SSE token from the BFF
    const token = await notificationApi.getSseToken();

    // Build URL with advisorId, brokerageId (database UUIDs), and token
    // Both IDs are database UUIDs - see design decision in class comment
    const streamPath = getSseStreamPath();
    const url = `${streamPath}?advisorId=${encodeURIComponent(identifiers.advisorId)}&brokerageId=${encodeURIComponent(identifiers.brokerageId)}&token=${encodeURIComponent(token)}`;

    return new EventSource(url, { withCredentials: true });
  },

  /**
   * Create SSE event source synchronously (for use cases where async isn't suitable)
   * Uses pre-fetched token and identifiers.
   *
   * IMPORTANT: Both advisorId and brokerageId are required for proper SSE registration.
   * Use getAdvisorIdentifiers() to fetch both before calling this method.
   *
   * @param token - SSE authentication token from getSseToken()
   * @param identifiers - Advisor identifiers from getAdvisorIdentifiers()
   */
  createEventSourceWithToken: (token: string, identifiers: AdvisorIdentifiers): EventSource => {
    const streamPath = getSseStreamPath();
    const url = `${streamPath}?advisorId=${encodeURIComponent(identifiers.advisorId)}&brokerageId=${encodeURIComponent(identifiers.brokerageId)}&token=${encodeURIComponent(token)}`;

    return new EventSource(url, { withCredentials: true });
  },

  /**
   * Check if using new Notification Service endpoints.
   * Useful for debugging and logging.
   */
  isUsingNewNotificationService: (): boolean => {
    return USE_NEW_NOTIFICATION_SERVICE;
  },
};

export default notificationApi;
